/**
 * 统一首页的呈现原语（2026-10-08）。
 *
 * 全部**无状态**：不持有 state、不发请求、不改业务语义。
 * 复用 `@/components/studio/ui` 的令牌（间距/字号/圆角/焦点环/按钮变体），
 * 🔴 而不是再抄一份 —— 本项目此前正是因为「同一个次级按钮在一个文件里写了 5 遍」
 * 导致改版要改 5 处、漏一处视觉就分叉。首页是新页面，正是最容易引入第二份
 * 设计系统的入口，所以令牌一律从 studio/ui 引用。
 *
 * 首页与工作台的**唯一**设计差异：首页是「读多写少」的看板，卡片之间要能一眼
 * 分组，所以额外加了 `HomeSection`（带标题栏的分区容器）与状态徽标。
 */

"use client";

import type { ReactNode } from "react";
import { AlertTriangle, Inbox, Loader2, RotateCw } from "lucide-react";

import {
  btn,
  F,
  FOCUS,
  H,
  iconBtn,
  panelCls,
  R,
  sectionHeadCls,
} from "@/components/studio/ui";

import type { ServiceHealth } from "@/lib/home/types";

// ── 分区容器 ────────────────────────────────────────────────────

/**
 * 首页分区。
 *
 * 🔴 为什么强制 `title` + 可选 `action` 成对出现：
 * 一个没有标题的卡片在看板里是**语义不明的色块** —— 用户无法判断
 * 「这张卡是趋势还是排行」。标题是分区唯一的语义锚点，不允许省略。
 */
/**
 * 首页分区。
 *
 * 🔴 为什么强制 `title` + 可选 `action` 成对出现：
 * 一个没有标题的卡片在看板里是**语义不明的色块** —— 用户无法判断
 * 「这张卡是趋势还是排行」。标题是分区唯一的语义锚点，不允许省略。
 *
 * 🔴 `tone` 为什么不走 className 覆盖：
 * 首版让指标区传 `className="bg-[var(--surface)]"` 想改底色，但 `panelCls`
 * 里已经含 `bg-white`，两个 `bg-*` 同权重共存时**胜负由 CSS 产物里的规则
 * 顺序决定，不由 class 属性里的书写顺序决定** —— 这与 ui.ts 里
 * `inputFixedCls` 记录的那次 `w-full` 胜出是同一类坑，只是换了个属性。
 * 所以底色做成**显式变体**：调用点选 `tone`，不写 bg-*。
 */
export function HomeSection({
  title,
  action,
  hint,
  footnote,
  className = "",
  bodyClassName = "",
  tone = "default",
  children,
}: {
  title: ReactNode;
  /** 标题行右侧插槽：口径说明、排序控件、刷新按钮 */
  action?: ReactNode;
  /** 标题下的口径说明（数据来源 + 统计口径） */
  hint?: ReactNode;
  /** 区块底部脚注（空态说明 / 失败说明） */
  footnote?: ReactNode;
  className?: string;
  bodyClassName?: string;
  /**
   * 底色层级：
   * · `default` 白底 —— 图表/面板等主要内容区；
   * · `sunken` 浅灰底 —— **装容器的分组**（指标条），内部再放白卡。
   *   全页白底白卡时每个区块都是同一种矩形，读不出分组。
   */
  tone?: "default" | "sunken";
  children: ReactNode;
}) {
  const toneCls = tone === "sunken" ? "!bg-[var(--surface)]" : "";
  return (
    <section className={`${panelCls} ${toneCls} flex min-w-0 flex-col ${className}`}>
      {/* 🔴 mb-1.5 → mb-1（2026-10-08 第 6 轮）：标题与正文之间 6px 就够。
          原来 6px 在通栏区块上看着「松」是因为区块本身太宽，
          但真正压密度的是**每处都省 2px** —— 首页有 8 个 HomeSection，
          竖向省 16px，比任何单点调整都明显。 */}
      <div className={`${sectionHeadCls} mb-1 flex-wrap`}>
        <div className="flex min-w-0 flex-col">
          <h2 className={`${F.sectionTitle} truncate`}>{title}</h2>
          {hint ? <p className={`${F.deco} mt-0.5 truncate normal-case`}>{hint}</p> : null}
        </div>
        {action ? <div className="flex shrink-0 items-center gap-1.5">{action}</div> : null}
      </div>
      {footnote ? <div className="mb-2">{footnote}</div> : null}
      <div className={`min-w-0 flex-1 ${bodyClassName}`}>{children}</div>
    </section>
  );
}

// ── 状态徽标 ────────────────────────────────────────────────────

const HEALTH_STYLE: Record<ServiceHealth, { dot: string; text: string; label: string }> = {
  ok: { dot: "bg-[var(--success)]", text: "text-[var(--success)]", label: "ok" },
  warn: { dot: "bg-[var(--warning)]", text: "text-[var(--warning)]", label: "warn" },
  error: { dot: "bg-[var(--destructive)]", text: "text-[var(--destructive)]", label: "error" },
  // 🔴 unknown 必须是**中性灰**，不能用 warning 黄：灰=「没探测到，别急」，
  // 黄=「有隐患，去看看」。两者混淆会让人对每一次刷新都紧张。
  unknown: { dot: "bg-[var(--text-muted)]", text: "text-[var(--text-secondary)]", label: "unknown" },
};

export function StatusDot({ health, pulse }: { health: ServiceHealth; pulse?: boolean }) {
  return (
    <span
      aria-hidden
      className={`inline-block h-2 w-2 shrink-0 rounded-full ${HEALTH_STYLE[health].dot} ${
        pulse ? "animate-status-pulse" : ""
      }`}
    />
  );
}

export function healthStyle(health: ServiceHealth) {
  return HEALTH_STYLE[health];
}

// ── 空态 / 错误态 / 加载 ────────────────────────────────────────

/**
 * 🔴 空态必须**常驻渲染 + 显式说明**。
 * 用 `{list.length > 0 && ...}` 门控会让整块消失，页面出现死区，
 * 用户以为页面坏了（studio 改版最先修的就是这个问题）。
 */
export function HomeEmpty({
  icon,
  title,
  hint,
  action,
  tone = "default",
  compact = false,
}: {
  icon?: ReactNode;
  title: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  tone?: "default" | "error";
  compact?: boolean;
}) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-[var(--border-subtle)] bg-[var(--surface)]/40 text-center ${
        compact ? "px-3 py-6" : "px-4 py-10"
      }`}
    >
      {icon ? (
        <span className={tone === "error" ? "text-[var(--destructive)]" : "text-[var(--text-muted)]"}>
          {icon}
        </span>
      ) : null}
      <p className={`${F.meta} max-w-[40ch] leading-relaxed`}>{title}</p>
      {hint ? <p className={`${F.deco} max-w-[44ch] leading-relaxed`}>{hint}</p> : null}
      {action ? <div className="mt-1.5">{action}</div> : null}
    </div>
  );
}

/** 默认空态图标：Inbox。抽出来是为了让「没有数据」在各处长得一样。 */
export function EmptyIcon({ className = "h-5 w-5" }: { className?: string }) {
  return <Inbox className={className} aria-hidden />;
}

/**
 * 加载骨架。
 *
 * 🔴 用 `animate-shimmer`（项目已有的 token）而不是 `animate-pulse`：
 * 后者是整体明暗闪烁，在多张卡同时加载时像警报；
 * shimmer 是横向流光，语义上就是「正在取数据」。
 */
export function HomeSkeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-shimmer rounded-lg ${className}`} aria-hidden />;
}

/** 区块级加载：占位形状按真实布局给，避免加载完成时布局跳动。 */
export function ChartSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="flex flex-col justify-end gap-2" style={{ height: 160 }} aria-hidden>
      {Array.from({ length: rows }, (_, i) => (
        <HomeSkeleton key={i} className="w-full" />
      ))}
    </div>
  );
}

/**
 * 加载失败块：**必须给出路**。
 * 只显示一句「加载失败」的话，用户只能猜该重试还是该重启服务。
 */
export function HomeError({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className="flex flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50/70 p-3"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-amber-900">加载失败</p>
          <p className="mt-0.5 text-xs leading-relaxed break-words text-amber-800">{message}</p>
        </div>
      </div>
      {onRetry ? (
        <div>
          <button
            type="button"
            onClick={onRetry}
            className={btn("outline", { size: "xs" })}
          >
            <RotateCw className="h-3 w-3" />
            重试
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function HomeSpinner({ className = "h-4 w-4" }: { className?: string }) {
  return <Loader2 className={`${className} shrink-0 animate-spin`} aria-hidden />;
}

// ── 小控件 ──────────────────────────────────────────────────────

/** 次级小按钮（区块标题栏内用：排序、刷新、置顶）。 */
export function HomeChip({
  active,
  children,
  ...rest
}: { active?: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={`${H.xs} ${R.control} shrink-0 px-2 text-xs font-medium transition-colors ${FOCUS} ${
        active
          ? "bg-[var(--primary)]/10 text-[var(--primary)]"
          : "text-[var(--text-secondary)] hover:bg-[var(--surface)] hover:text-[var(--text-primary)]"
      }`}
      {...rest}
    >
      {children}
    </button>
  );
}

/** 纯图标小按钮（刷新、置顶星标）。 */
export function HomeIconBtn({
  label,
  active,
  children,
  ...rest
}: { label: string; active?: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active}
      className={`${iconBtn({ size: "xs" })} ${active ? "text-[var(--primary)]" : ""}`}
      {...rest}
    >
      {children}
    </button>
  );
}
